// Mirrors backend enum: EstadoFirmante
export enum EEstadoFirmante {
  Iniciado = "IN",
  EnProceso = "PE",
  Completado = "CO",
  Cancelado = "CA",
  Rechazado = "DE",
}

// Mirrors backend enum: TipoRelacionPersona (MG.Common.Business.Enums) [EnumAsInt]
export enum ETipoRelacionPersona {
  Titular = "Titular",
  Conyuge = "Conyuge",
  Codeudor = "Codeudor",
  Conyuge_del_Codeudor = "Conyuge_del_Codeudor",
  Fiador = "Fiador",
  Padre_o_madre = "Padre_o_madre",
  Hijo = "Hijo",
}

const TIPO_RELACION_PERSONA_FROM_INT: Record<number, ETipoRelacionPersona> = {
  1: ETipoRelacionPersona.Titular,
  2: ETipoRelacionPersona.Conyuge,
  3: ETipoRelacionPersona.Codeudor,
  4: ETipoRelacionPersona.Conyuge_del_Codeudor,
  5: ETipoRelacionPersona.Fiador,
  6: ETipoRelacionPersona.Padre_o_madre,
  7: ETipoRelacionPersona.Hijo,
};

const TIPO_RELACION_PERSONA_TO_INT: Record<ETipoRelacionPersona, number> = {
  [ETipoRelacionPersona.Titular]: 1,
  [ETipoRelacionPersona.Conyuge]: 2,
  [ETipoRelacionPersona.Codeudor]: 3,
  [ETipoRelacionPersona.Conyuge_del_Codeudor]: 4,
  [ETipoRelacionPersona.Fiador]: 5,
  [ETipoRelacionPersona.Padre_o_madre]: 6,
  [ETipoRelacionPersona.Hijo]: 7,
};

const TIPOS_RELACION_PERSONA: ReadonlySet<string> = new Set(
  Object.values(ETipoRelacionPersona),
);

export function isTipoRelacionPersona(
  value: unknown,
): value is ETipoRelacionPersona {
  return typeof value === "string" && TIPOS_RELACION_PERSONA.has(value);
}

// Compatibilidad con endpoints en INT y respuestas en string.
export function normalizeTipoRelacionPersona(
  value: number | string | null | undefined,
): ETipoRelacionPersona | undefined {
  if (value == null) return undefined;
  if (typeof value === "number") {
    return TIPO_RELACION_PERSONA_FROM_INT[value];
  }
  return isTipoRelacionPersona(value) ? value : undefined;
}

// Para endpoints que esperan el tipo de relación como INT.
export function getTipoRelacionPersonaCode(
  value: number | string | null | undefined,
): number | undefined {
  const tipo = normalizeTipoRelacionPersona(value);
  return tipo ? TIPO_RELACION_PERSONA_TO_INT[tipo] : undefined;
}

export enum EEstadoSolicitudPrestamoConsumo {
  EnAnalisis = "En_Analisis",
  Aprobada = "Aprobada",
  Rechazada = "Rechazada",
  Liquidada = "Liquidada",
  Incompleta = "Incompleta",
  Desistida = "Desistida",
}

export enum ETipoCredito {
  Prendario = "Prendario",
  Personal = "Personal",
  Autoprenda = "Autoprenda",
  Consumo = "Consumo",
}

const TIPO_CREDITO_FROM_INT: Record<number, ETipoCredito> = {
  1: ETipoCredito.Prendario,
  2: ETipoCredito.Personal,
  3: ETipoCredito.Autoprenda,
  4: ETipoCredito.Consumo,
};

// Compatibilidad con endpoints en INT.
export function normalizeTipoCredito(
  value: number | string | null | undefined,
): ETipoCredito | undefined {
  if (value == null) return undefined;
  if (typeof value === "number") {
    return TIPO_CREDITO_FROM_INT[value];
  }
  return value as ETipoCredito;
}

export enum ELineaProducto {
  Blanca = "Blanca",
  Electro = "Electro",
  Celulares = "Celulares",
  Motos = "Motos",
  Movilidad = "Movilidad",
}

const LINEA_PRODUCTO_FROM_INT: Record<number, ELineaProducto> = {
  1: ELineaProducto.Blanca,
  2: ELineaProducto.Electro,
  3: ELineaProducto.Celulares,
  4: ELineaProducto.Motos,
  5: ELineaProducto.Movilidad,
};

const LINEA_PRODUCTO_TO_INT: Record<ELineaProducto, number> = {
  [ELineaProducto.Blanca]: 1,
  [ELineaProducto.Electro]: 2,
  [ELineaProducto.Celulares]: 3,
  [ELineaProducto.Motos]: 4,
  [ELineaProducto.Movilidad]: 5,
};

export function normalizeLineaProducto(
  value: number | string | null | undefined,
): ELineaProducto | undefined {
  if (value == null) return undefined;
  if (typeof value === "number") {
    return LINEA_PRODUCTO_FROM_INT[value];
  }
  if (/^\d+$/.test(value)) {
    return LINEA_PRODUCTO_FROM_INT[Number(value)];
  }
  return value as ELineaProducto;
}

export const isLineaMotos = (
  value: number | string | null | undefined,
): boolean => normalizeLineaProducto(value) === ELineaProducto.Motos;

export const isLineaMovilidad = (
  value: number | string | null | undefined,
): boolean => normalizeLineaProducto(value) === ELineaProducto.Movilidad;

export const getLineaProductoCode = (
  value: number | string | null | undefined,
): number => {
  const linea = normalizeLineaProducto(value);
  if (linea && linea in LINEA_PRODUCTO_TO_INT) {
    return LINEA_PRODUCTO_TO_INT[linea];
  }
  return LINEA_PRODUCTO_TO_INT[ELineaProducto.Blanca];
};

// Menor rank = mayor precedencia. Orden: Celulares > Electro > Motos > Movilidad > Blanca.
const LINEA_PRECEDENCE_RANK: Record<ELineaProducto, number> = {
  [ELineaProducto.Celulares]: 1,
  [ELineaProducto.Electro]: 2,
  [ELineaProducto.Motos]: 3,
  [ELineaProducto.Movilidad]: 4,
  [ELineaProducto.Blanca]: 5,
};

export const getLineaConPrecedencia = (selectedCodes: number[]): number => {
  let winner: ELineaProducto = ELineaProducto.Blanca;
  for (const code of selectedCodes) {
    const linea = LINEA_PRODUCTO_FROM_INT[code];
    if (linea && LINEA_PRECEDENCE_RANK[linea] < LINEA_PRECEDENCE_RANK[winner]) {
      winner = linea;
    }
  }
  return LINEA_PRODUCTO_TO_INT[winner];
};

export enum ETipoUsuario {
  Interno = "Interno",
  Agenciero = "Agenciero",
  ClienteFinal = "ClienteFinal",
  Productor = "Productor",
  Gestor = "Gestor",
  Vendedor = "Vendedor",
  Supervisor = "Supervisor",
}

const TIPOS_USUARIO: ReadonlySet<string> = new Set(Object.values(ETipoUsuario));

export function isTipoUsuario(value: unknown): value is ETipoUsuario {
  return typeof value === "string" && TIPOS_USUARIO.has(value);
}

export function normalizeTipoUsuario(value: unknown): ETipoUsuario | undefined {
  return isTipoUsuario(value) ? value : undefined;
}

// Mirrors backend enum: ESituacionOperacion (MG.Common.Business.Enums) [EnumAsInt]
export enum ESituacionOperacion {
  //solicitudes
  Carga_Incompleta = "Carga_Incompleta",
  Informes_Pendientes = "Informes_Pendientes",
  Rechazada = "Rechazada",
  Rechazada_Definitivamente = "Rechazada_Definitivamente",
  En_Analisis = "En_Analisis",
  No_Apto_Habilitado = "No_Apto_Habilitado",
  Aprobada = "Aprobada",
  Confeccion_Solicitada = "Confeccion_Solicitada",
  Prenda_Confeccionada = "Prenda_Confeccionada",
  Liquidacion_solicitada = "Liquidacion_solicitada",
  Desistida = "Desistida",
  Desistida_por_tiempo = "Desistida_por_tiempo",
  Liquidada = "Liquidada",
  En_Espera_Informes_Riesgo_Vehiculo = "En_Espera_Informes_Riesgo_Vehiculo",
  Con_Vehiculo_Aprobado = "Con_Vehiculo_Aprobado",

  //creditos
  Cancelado = "Cancelado",
  Cancelado_Anticipadamente = "Cancelado_Anticipadamente",
  Cancelado_Anticipadamente_Judicial = "Cancelado_Anticipadamente_Judicial",
  Siniestro_en_tramite = "Siniestro_en_tramite",
  Siniestro_pendiente_desembolso = "Siniestro_pendiente_desembolso",
  Dacion_En_Pago = "Dacion_En_Pago",
  Finalizado = "Finalizado",
  Plazo_completado = "Plazo_completado",

  //Otras entidades
  Para_ofrecer_otras_entidades = "Para_ofrecer_otras_entidades",
  Cargada_otras_entidades = "Cargada_otras_entidades",
  Rechazada_otras_entidades = "Rechazada_otras_entidades",
  Aprobada_otras_entidades = "Aprobada_otras_entidades",
  Para_prendar_otras_entidades = "Para_prendar_otras_entidades",
  En_espera_liquidacion_otras_entidades = "En_espera_liquidacion_otras_entidades",
  Liquidada_otras_entidades = "Liquidada_otras_entidades",
}

export const SITUACION_OPERACION_CODES: Readonly<
  Record<ESituacionOperacion, number>
> = {
  //solicitudes
  [ESituacionOperacion.Carga_Incompleta]: 1,
  [ESituacionOperacion.Informes_Pendientes]: 4,
  [ESituacionOperacion.Rechazada]: 6,
  [ESituacionOperacion.Rechazada_Definitivamente]: 7,
  [ESituacionOperacion.En_Analisis]: 92,
  [ESituacionOperacion.No_Apto_Habilitado]: 99,
  [ESituacionOperacion.Aprobada]: 18,
  [ESituacionOperacion.Confeccion_Solicitada]: 80,
  [ESituacionOperacion.Prenda_Confeccionada]: 16,
  [ESituacionOperacion.Liquidacion_solicitada]: 81,
  [ESituacionOperacion.Desistida]: 19,
  [ESituacionOperacion.Desistida_por_tiempo]: 20,
  [ESituacionOperacion.Liquidada]: 8,
  [ESituacionOperacion.En_Espera_Informes_Riesgo_Vehiculo]: 83,
  [ESituacionOperacion.Con_Vehiculo_Aprobado]: 84,

  //creditos
  [ESituacionOperacion.Cancelado]: 11,
  [ESituacionOperacion.Cancelado_Anticipadamente]: 13,
  [ESituacionOperacion.Cancelado_Anticipadamente_Judicial]: 25,
  [ESituacionOperacion.Siniestro_en_tramite]: 50,
  [ESituacionOperacion.Siniestro_pendiente_desembolso]: 51,
  [ESituacionOperacion.Dacion_En_Pago]: 59,
  [ESituacionOperacion.Finalizado]: 60,
  [ESituacionOperacion.Plazo_completado]: 70,

  //Otras entidades
  [ESituacionOperacion.Para_ofrecer_otras_entidades]: 61,
  [ESituacionOperacion.Cargada_otras_entidades]: 62,
  [ESituacionOperacion.Rechazada_otras_entidades]: 63,
  [ESituacionOperacion.Aprobada_otras_entidades]: 64,
  [ESituacionOperacion.Para_prendar_otras_entidades]: 65,
  [ESituacionOperacion.En_espera_liquidacion_otras_entidades]: 66,
  [ESituacionOperacion.Liquidada_otras_entidades]: 67,
};

const SITUACIONES_OPERACION: ReadonlySet<string> = new Set(
  Object.values(ESituacionOperacion),
);

export function isSituacionOperacion(
  value: unknown,
): value is ESituacionOperacion {
  return typeof value === "string" && SITUACIONES_OPERACION.has(value);
}

// Mirrors backend enum: EEstadoTareaOperacion (MG.Common.Business.Enums) [EnumAsInt]
export enum EEstadoTareaOperacion {
  Pendiente = "Pendiente",
  Para_Analizar = "Para_Analizar",
  OK = "OK",
  Postergada = "Postergada",
  No_Completada = "No_Completada",
  Excepcionada = "Excepcionada",
}

// Mirrors backend enum: ETipoTareaSolicitud (MG.Common.Business.Enums)
export enum ETipoTareaSolicitud {
  Archivo = "Archivo",
  Telefono = "Telefono",
  Informacion = "Informacion",
}

// Mirrors backend enum: ETipoTelefono (MG.Common.Business.Enums) [EnumAsInt]
export enum ETipoTelefono {
  Particular = "Particular",
  Laboral = "Laboral",
  Conyuge = "Conyuge",
  Padre = "Padre",
  Hijo = "Hijo",
  Hermano = "Hermano",
  Familiar = "Familiar",
  Amigo = "Amigo",
  Vecino = "Vecino",
  Otro = "Otro",
  Garante = "Garante",
}

// Mirrors backend table: categoriatarea. Solo se declaran las categorías que la UI distingue.
export enum ECategoriaTarea {
  Conyuge = 3,
  Codeudor = 4,
}

// Mirrors backend column: tipopoliza.tipoCobertura (varchar), emitido por Originación/PrendarioApi.
export enum ETipoPoliza {
  TercerosCompleto = "TCBase",
  TercerosPremium = "TCPremium",
  TodoRiesgo = "TR",
  ResponsabilidadCivil = "RC",
}

const TIPOS_POLIZA: ReadonlySet<string> = new Set(Object.values(ETipoPoliza));

export function isTipoPoliza(value: unknown): value is ETipoPoliza {
  return typeof value === "string" && TIPOS_POLIZA.has(value);
}

// Mirrors backend enum: TipoCambio (MG.Common.Business.Enums)
export enum ETipoPedidoCambio {
  Plazo_y_Monto = "Plazo_y_Monto",
  Fec_Nac_y_Nombre = "Fec_Nac_y_Nombre",
  Estado_Civil = "Estado_Civil",
  Domicilio = "Domicilio",
  Datos_de_Contacto = "Datos_de_Contacto",
  Vehiculo = "Vehiculo",
  Seguro_Cobertura = "Seguro_Cobertura",
}

const TIPOS_PEDIDO_CAMBIO: ReadonlySet<string> = new Set(
  Object.values(ETipoPedidoCambio),
);

export function isTipoPedidoCambio(value: unknown): value is ETipoPedidoCambio {
  return typeof value === "string" && TIPOS_PEDIDO_CAMBIO.has(value);
}

// Mirrors backend enum: EstadoCambioOperacion (MG.Common.Business.Enums)
export enum EEstadoPedidoCambio {
  Pendiente = "Pendiente",
  Rechazado = "Rechazado",
  Aplicado = "Aplicado",
}

const ESTADOS_PEDIDO_CAMBIO: ReadonlySet<string> = new Set(
  Object.values(EEstadoPedidoCambio),
);

export function isEstadoPedidoCambio(
  value: unknown,
): value is EEstadoPedidoCambio {
  return typeof value === "string" && ESTADOS_PEDIDO_CAMBIO.has(value);
}
